import type { AutomationRule, PaymentMatchValue, RuleMatch } from './worker-automation.types';

/**
 * Payment blocks: a CARC group narrows a CARC code, it cannot match on its own.
 *
 * On an 835 the CAS segment pairs a group code with the reason code, and the pair carries the full
 * meaning — `CO-45` is a contractual write-off, `PR-45` is owed by the patient. A group alone matches
 * every adjustment in that group, which is most of a remittance, so it is never a filter.
 *
 * A code alone IS a filter — "45 wherever it lands" — and the customer asked for it explicitly
 * (2026-09-21), overriding the original pairing rule. It is broader than the pair: a rule on `45`
 * fires on `CO-45` and `PR-45` alike, which are opposite outcomes (written off vs owed by the
 * patient). That breadth is the customer's call to make, not the form's.
 *
 * A RARC stands on its own. So the authoring rule is: a CARC code, with or without a group, or a
 * RARC code — and a group with no code stays an error even when a RARC is present, because the
 * group still matches nothing by itself.
 *
 * This checks what the form requires, not how the worker evaluates a rule — that is backend.
 *
 * Kept as a leaf with type-only imports on purpose: the scope registry reaches `ui/data-table`, which
 * the DOM-free `jest.services.config.js` cannot load, so the tests import this file directly.
 */

interface PaymentLists {
    carcCodes: string[];
    carcGroups: string[];
    rarcCodes: string[];
}

function nonBlank(values: unknown): string[] {
    return Array.isArray(values) ? values.map(value => String(value).trim()).filter(Boolean) : [];
}

function readPaymentLists(value: unknown): PaymentLists {
    const payment = (value ?? {}) as Partial<PaymentMatchValue>;

    return {
        carcGroups: nonBlank(payment.carc?.group),
        carcCodes: nonBlank(payment.carc?.codes),
        rarcCodes: nonBlank(payment.rarc?.codes),
    };
}

/** A CARC code (group optional), or a RARC code. A group with no code is never valid. */
export function getCarcPairValidationError(value: unknown): string | null {
    const { carcGroups, carcCodes, rarcCodes } = readPaymentLists(value);
    const hasGroup = carcGroups.length > 0;
    const hasCode = carcCodes.length > 0;

    if (hasGroup && !hasCode) {
        return 'A CARC group needs a CARC code.';
    }
    if (!hasCode && rarcCodes.length === 0) {
        return 'Add a CARC code, or a RARC code.';
    }

    return null;
}

/** Order-insensitive identity of a Payment block's values. */
function paymentFingerprint(match: RuleMatch): string {
    const { carcGroups, carcCodes, rarcCodes } = readPaymentLists(match.value);
    const sorted = (values: string[]): string[] => [...values].sort();

    return JSON.stringify([sorted(carcGroups), sorted(carcCodes), sorted(rarcCodes)]);
}

/**
 * For each block of a rule, whether it must satisfy the CARC pair rule — i.e. whether it is new or edited.
 *
 * Untouched saved blocks keep the older "at least one field" rule. Save validation covers every rule
 * on the account, so without this a single legacy group-only rule would block saving anything.
 *
 * A block is exempt only when the SAME block — matched by `uid`, assigned on hydration and on add —
 * was saved as a Payment block with the same values. Values alone cannot say which block is which:
 * deleting a legacy `CO`-only block and adding a new one, or moving the saved block to another scope
 * and adding a Payment block beside it, both leave the draft looking untouched, and the new invalid
 * block used to inherit the deleted block's exemption (PR #530 review).
 *
 * Blocks with no `uid` fall back to value matching, where each saved block exempts at most ONE draft
 * block, consumed in rule order. That path is for callers that build matches directly; everything the
 * store hydrates carries a uid. Positions are never used: removing a block shifts every index after
 * it, which would mark untouched blocks as edited.
 */
export function getCarcPairRequirements(
    ruleId: string,
    matches: RuleMatch[],
    savedRules: AutomationRule[] | undefined,
): boolean[] {
    const savedRule = savedRules?.find(rule => rule.id === ruleId);
    const savedByUid = new Map<string, string>();
    const unclaimed = new Map<string, number>();

    for (const saved of savedRule?.match ?? []) {
        if (saved.scope !== 'PAYMENT') {
            continue;
        }

        const fingerprint = paymentFingerprint(saved);
        if (saved.uid) {
            savedByUid.set(saved.uid, fingerprint);
        } else {
            unclaimed.set(fingerprint, (unclaimed.get(fingerprint) ?? 0) + 1);
        }
    }

    const claimedUids = new Set<string>();

    return matches.map(match => {
        if (match.scope !== 'PAYMENT') {
            return false;
        }

        const fingerprint = paymentFingerprint(match);

        if (match.uid) {
            const savedFingerprint = savedByUid.get(match.uid);
            if (savedFingerprint === undefined || claimedUids.has(match.uid)) {
                return true;
            }

            claimedUids.add(match.uid);
            return savedFingerprint !== fingerprint;
        }

        const left = unclaimed.get(fingerprint) ?? 0;
        if (left === 0) {
            return true;
        }

        unclaimed.set(fingerprint, left - 1);
        return false;
    });
}
